import { FC, useEffect, useRef } from 'react';

import { CAMPAIGN_ROOT_REGION_ID } from 'utils/constants/campaign';

import cpcFlexibleExceptMoscowButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/cpc_flexible_except_moscow_button_click';
import { Link, Text, TreeCollection, VSpacing } from '@hh.ru/magritte-ui';
import { QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import LocalStorageWrapper from 'bloko/common/storage/LocalStorageWrapper';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useIsNewCampaign from 'utils/campaign/hooks/useIsNewCampaign';

import CompositeSelection from 'components/common/CompositeSelection/CompositeSelection';
import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';
import { AdRegionsInput } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/AdRegionsInput/AdRegionsInput';
import { clearRegions } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/clearRegions';
import { ExcludedRegionsInfoTip } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/ExcludedRegionsInfoTip/ExcludedRegionsInfoTip';
import { InfoRegionsTip } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/InfoRegionsTip/InfoRegionsTip';
import { PricePerClickLabel } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/PricePerClickLabel/PricePerClickLabel';
import { setAllRegionsWithoutMoscow } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/setAllRegionsWithoutMoscow';
import { useData } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/useData';
import { getIncludedAndExcludedRegionsIds } from 'components/CpcFlexibleCreate/CpcForm/AdRegions/utils';

import {
    selectCampaignExcludedRegions,
    selectCampaignRegions,
    selectIsRootRegionSelected,
    setCampaignExcludedRegions,
    setCampaignRegions,
    setIsRootRegionSelected,
} from 'store/models/createCampaign';
import { selectMainMenuVacancyId } from 'store/models/mainMenu';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CpcFlexibleCreate/CpcForm/AdRegions/AdRegions.module.less';

const INFO_REGIONS_STORAGE = 'INFO_REGIONS';

const TrlKeys = {
    legend: 'AdCreate.AdForm.Regions.legend',
    priceTip: 'AdCreate.AdForm.Regions.Price.tip',
    modal: {
        title: 'AdCreate.AdForm.Regions.Popup.title',
        submit: 'AdCreate.AdForm.Regions.Popup.submit',
        cancel: 'AdCreate.AdForm.Regions.Popup.cancel',
    },
    excludedTitle: 'AdCreate.AdForm.Regions.ExcludedTitleMagritte',
    allRegionsWithoutMoscow: 'AdCreate.AdForm.Regions.Popup.AllRegionsWithoutMoscowMagritte',
    clear: 'AdCreate.AdForm.Regions.clearMagritte',
};

export const AdRegions: FC = () => {
    const vacancyId = useSelector(selectMainMenuVacancyId);
    const adRegions = useSelector(selectCampaignRegions);
    const excludedAdRegions = useSelector(selectCampaignExcludedRegions);
    const isRootRegionSelected = useSelector(selectIsRootRegionSelected);

    const [isShowInfo, setShowInfoOn, setShowInfoOff] = useOnOffState(
        !LocalStorageWrapper.getItem(INFO_REGIONS_STORAGE)
    );

    const excludedRegionsInfoTipActivatorRef = useRef<HTMLDivElement>(null);
    const infoRegionsActivatorRef = useRef<HTMLDivElement>(null);
    const isCampaignProcessCreate = useIsNewCampaign();
    const dispatch = useDispatch();
    const trl = useTrls();

    useEffect(() => {
        if (!LocalStorageWrapper.getItem(INFO_REGIONS_STORAGE)) {
            setShowInfoOn();
            LocalStorageWrapper.setItem(INFO_REGIONS_STORAGE, 'true');
        }
    }, [setShowInfoOn]);

    const data = useData();

    const onRegionsChange = (selectedRegions: string[], excludedRegions?: string[]) => {
        const regions = [...selectedRegions];

        const isRootRegionRemoved =
            adRegions.includes(CAMPAIGN_ROOT_REGION_ID) && !regions.includes(CAMPAIGN_ROOT_REGION_ID);

        if (isRootRegionRemoved) {
            dispatch(setIsRootRegionSelected(false));
        } else if (isRootRegionSelected && !regions.includes(CAMPAIGN_ROOT_REGION_ID) && regions.length > 0) {
            regions.unshift(CAMPAIGN_ROOT_REGION_ID);
        }

        const { includedRegions: included, excludedRegions: excluded } = getIncludedAndExcludedRegionsIds(
            regions,
            excludedRegions ?? ([] as string[]),
            data.collection as TreeCollection
        );

        dispatch([setCampaignRegions(included), setCampaignExcludedRegions(excluded)]);
    };

    return (
        <>
            <MagritteFormField
                // в компоненте MagritteFormField реализован tooltipHover, а здесь нужен tooltip
                // из-за наличия ссылки внутри, поэтому здесь передаем текст с иконкой
                label={
                    <div className={styles.label}>
                        <Text typography="label-2-regular" style="secondary">
                            {trl(TrlKeys.legend)}
                        </Text>
                        <div className={styles.labelIcon} ref={infoRegionsActivatorRef}>
                            <QuestionCircleOutlinedSize16
                                initialColor="secondary"
                                onClick={() => {
                                    setShowInfoOn();
                                    if (!LocalStorageWrapper.getItem(INFO_REGIONS_STORAGE)) {
                                        LocalStorageWrapper.setItem(INFO_REGIONS_STORAGE, 'true');
                                    }
                                }}
                            />
                        </div>
                    </div>
                }
                input={
                    <CompositeSelection.Root
                        collection={data.collection}
                        value={adRegions}
                        excludedValue={excludedAdRegions}
                        onChange={onRegionsChange}
                    >
                        <AdRegionsInput provider={data.provider} />
                        <CompositeSelection.TreeSelectorModal
                            texts={{
                                title: trl(TrlKeys.modal.title),
                                submit: trl(TrlKeys.modal.submit),
                                cancel: trl(TrlKeys.modal.cancel),
                            }}
                            rootElementControl={(state) => {
                                dispatch(setIsRootRegionSelected(state));
                            }}
                        />
                        {adRegions.length > 0 ? (
                            <>
                                <VSpacing default={20} />
                                <CompositeSelection.Chips containerDataQa="regions-chips-container" />
                                <VSpacing default={20} />
                                <Link
                                    typography="label-2-regular"
                                    Element="button"
                                    onClick={() => dispatch(clearRegions())}
                                    data-qa="clear-regions"
                                >
                                    {trl(TrlKeys.clear)}
                                </Link>
                                {excludedAdRegions.length > 0 && (
                                    <>
                                        <VSpacing default={24} />
                                        <div ref={excludedRegionsInfoTipActivatorRef}>
                                            <CompositeSelection.ExcludedChips
                                                title={trl(TrlKeys.excludedTitle)}
                                                containerDataQa="regions-excluded-chips-container"
                                            />
                                        </div>
                                        <ExcludedRegionsInfoTip activatorRef={excludedRegionsInfoTipActivatorRef} />
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <VSpacing default={12} />
                                <Link
                                    Element="button"
                                    typography="label-2-regular"
                                    onClick={() => {
                                        if (isCampaignProcessCreate) {
                                            cpcFlexibleExceptMoscowButtonClick({ vacancyId });
                                        }
                                        dispatch(setAllRegionsWithoutMoscow());
                                    }}
                                    data-qa="select-all-regions-without-moscow"
                                >
                                    {trl(TrlKeys.allRegionsWithoutMoscow)}
                                </Link>
                            </>
                        )}
                    </CompositeSelection.Root>
                }
            />
            <VSpacing default={32} />
            <PricePerClickLabel />
            <VSpacing default={32} />
            <InfoRegionsTip
                activatorRef={infoRegionsActivatorRef}
                visible={isShowInfo}
                onClose={setShowInfoOff}
                label={trl(TrlKeys.legend)}
            />
        </>
    );
};
